import { Input, Button, message, type GetRef } from 'antd';
import { SendOutlined, PaperClipOutlined, CloseOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styles from './Chat.module.scss';

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.csv', '.txt'];
const ACCEPTED_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
  'application/zip', 
]);
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_FILES_PER_THREAD = 10;

export interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  sending: boolean;
  placeholder?: string;
  variant?: 'welcome' | 'floating';
  attachedFiles?: File[];
  onFilesAttach?: (files: File[]) => void;
  onFileRemove?: (index: number) => void;
  threadFileCount?: number;
}

type TextAreaRef = GetRef<typeof Input.TextArea>;

const MAX_ROWS = 8;

const ChatInput = ({
  value,
  onChange,
  onSend,
  sending,
  placeholder = 'Ask something to Rubix...',
  variant = 'welcome',
  attachedFiles = [],
  onFilesAttach,
  onFileRemove,
  threadFileCount = 0,
}: ChatInputProps) => {
  const textAreaRef = useRef<TextAreaRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const minRows = variant === 'welcome' ? 2 : 1;
  const remainingSlots = MAX_FILES_PER_THREAD - threadFileCount - attachedFiles.length;

  // CSP-safe auto-grow: antd's `autoSize` uses rc-textarea, which sizes a
  // measurement node via `element.setAttribute('style', …)`. That is an inline
  // *style attribute*, which a CSP nonce cannot authorize, so it is blocked
  // under a strict `style-src`. Writing individual properties through the CSSOM
  // (`element.style.height = …`) is exempt from `style-src`, so we grow the
  // textarea imperatively instead.
  const autoGrow = useCallback(() => {
    const el = textAreaRef.current?.resizableTextArea?.textArea;
    if (!el) return;
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 20;
    const paddingY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const borderY = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const minHeight = lineHeight * minRows + paddingY + borderY;
    const maxHeight = lineHeight * MAX_ROWS + paddingY + borderY;
    const contentHeight = el.scrollHeight + borderY;
    const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }, [minRows]);

  useLayoutEffect(() => {
    autoGrow();
  }, [value, autoGrow]);

  useEffect(() => {
    window.addEventListener('resize', autoGrow);
    return () => window.removeEventListener('resize', autoGrow);
  }, [autoGrow]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      onSend();
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same file after removal
    if (selected.length === 0) return;

    const rejected: string[] = [];
    const accepted: File[] = [];
    for (const file of selected) {
      const dot = file.name.lastIndexOf('.');
      const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
      const validType = ACCEPTED_EXTENSIONS.includes(ext) && (!file.type || ACCEPTED_MIME_TYPES.has(file.type));
      if (!validType) {
        rejected.push(`${file.name} (unsupported file type)`);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        rejected.push(`${file.name} (exceeds 50 MB)`);
      } else {
        accepted.push(file);
      }
    }

    const toAttach = accepted.slice(0, Math.max(remainingSlots, 0));
    rejected.push(
      ...accepted.slice(toAttach.length).map((f) => `${f.name} (thread limit of ${MAX_FILES_PER_THREAD} files reached)`)
    );

    if (rejected.length > 0) {
      message.error(`Could not attach: ${rejected.join(', ')}`);
    }
    if (toAttach.length > 0) {
      onFilesAttach?.(toAttach);
    }
  };

  const wrapperClass = variant === 'welcome' ? styles.inputWrapperWelcome : styles.inputWrapperFloating;
  const buttonClass = variant === 'welcome' ? styles.sendButtonWelcome : styles.sendButtonFloating;
  const textAreaClass = variant === 'floating' ? styles.textAreaFloating : styles.textArea;
 

  return (
    <div className={styles.inputGroup}>
      <div className={wrapperClass}>
        {attachedFiles.length > 0 && (
          <div className={styles.attachedFilesRow}>
            {attachedFiles.map((file, index) => (
              <span key={`${file.name}-${index}`} className={styles.fileChip}>
                <span className={styles.fileChipName}>{file.name}</span>
                <button
                  type="button"
                  className={styles.fileChipRemove}
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onFileRemove?.(index)}
                >
                  <CloseOutlined />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className={styles.inputRow}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS.join(',')}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          <Button
            type="text"
            icon={<PaperClipOutlined />}
            onClick={handleAttachClick}
            disabled={sending || remainingSlots <= 0}
            className={styles.uploadButton}
            title="Attach files"
          />
          <Input.TextArea
            ref={textAreaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={minRows}
            onPressEnter={handleKeyPress}
            placeholder={placeholder}
            className={textAreaClass}
          />
          <Button
            type="primary"
            icon={!sending ? <SendOutlined /> : undefined}
            onClick={onSend}
            disabled={(!value.trim() && attachedFiles.length === 0) || sending}
            className={buttonClass}
          >
            {sending ? (
              <span className={styles.loadingDots}>
                {['.', '.', '.'].map((d, i) => (
                  <span key={i} className={styles.loadingDot}>
                    {d}
                  </span>
                ))}
              </span>
            ) : (
              'Send'
            )}
          </Button>
        </div>
      </div>
      {variant === 'floating' && (
        <div className={styles.inputDisclaimer}>
          AI-generated content may be incorrect
        </div>
      )}
    </div>
  );
};

export default ChatInput;
